import { Button } from '../../components/Button';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { ActionBar } from '../ActionBar';
import { BookingNotesCard } from '../BookingNotesCard';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import { ContentPanelShell, HeadingAccent } from '../ContentPanelShell';
import { PanelActionFooter } from '../PanelActionFooter';
import { AddGuestRow, FormGroup } from './GuestDetailsContentPanel.styles';
import type { GuestDetailsContentPanelProps } from './GuestDetailsContentPanel.types';
import { radius } from '../../theme/tokens';

export function GuestDetailsContentPanel({
  guests,
  onGuestChange,
  onExistingGuestSelect,
  existingGuests,
  titleOptions,
  genderOptions,
  maxGuests = 2,
  onAddGuest,
  onRemoveGuest,
  requireAllGuests = false,
  showNotes = true,
  notesChecked = false,
  onNotesToggle,
  notesValue = '',
  onNotesChange,
  onBack,
  currentStep = 1,
  totalSteps,
  footerTitle,
  totalAmount,
  currencyCode,
  locale = 'en-GB',
  onNext,
  nextDisabled = false,
  isLoading = false,
  labels = {},
}: GuestDetailsContentPanelProps) {
  const {
    title = 'Guest details.',
    titleAccent = 'Just a name to hold your room.',
    backAriaLabel = 'Go back',
    progressLabel = 'Booking progress',
    guestTitleFormatter,
    guestSubtitle,
    dividerText,
    titleLabel,
    titlePlaceholder,
    firstNameLabel,
    lastNameLabel,
    addGuest = 'Add guest',
    removeGuest = 'Remove',
    notesTitle,
    notesPlaceholder,
    totalLabel = 'Total',
    nextLabel = 'Next',
  } = labels;

  const canAddGuest = !!onAddGuest && guests.length < maxGuests;

  const selectedIdsByIndex = guests.map((guest) => guest.selectedExistingGuestId);

  const isGuestRequired = (index: number) => index === 0 || requireAllGuests;

  return (
    <ContentPanelShell
      isLoading={isLoading}
      topBar={
        <ActionBar
          onBack={onBack}
          backAriaLabel={backAriaLabel}
          currentStep={currentStep}
          totalSteps={totalSteps}
          progressLabel={progressLabel}
        />
      }
      heading={
        <>
          {title} <HeadingAccent>{titleAccent}</HeadingAccent>
        </>
      }
      footer={
        <PanelActionFooter
          title={footerTitle}
          totalLabel={totalLabel}
          totalAmount={totalAmount}
          currencyCode={currencyCode}
          locale={locale}
          ctaLabel={nextLabel}
          onCtaClick={onNext}
          ctaDisabled={nextDisabled}
          isLoading={isLoading}
        />
      }
    >
      {isLoading ? (
        <FormGroup>
          <SkeletonBlock height="56px" borderRadius={radius('input', 'radius-md')} />
          <SkeletonBlock height="56px" borderRadius={radius('input', 'radius-md')} />
          <SkeletonBlock height="56px" borderRadius={radius('input', 'radius-md')} />
          <SkeletonBlock height="120px" borderRadius={radius('input', 'radius-md')} />
        </FormGroup>
      ) : (
        <FormGroup>
          {guests.map((guest, index) => (
            <GuestDetailsPanel
              key={index}
              guestNumber={index + 1}
              showHeader={index > 0}
              subtitle={guestSubtitle}
              onRemove={index > 0 && onRemoveGuest ? () => onRemoveGuest(index) : undefined}
              existingGuests={existingGuests}
              selectedGuestId={guest.selectedExistingGuestId}
              disabledGuestIds={selectedIdsByIndex.filter(
                (id, otherIndex): id is string => !!id && otherIndex !== index
              )}
              onExistingGuestSelect={(guestId) => onExistingGuestSelect?.(index, guestId)}
              values={guest.values}
              errors={guest.errors}
              onChange={(field, value) => onGuestChange(index, field, value)}
              titleOptions={titleOptions}
              genderOptions={genderOptions}
              showGender={false}
              dividerText={dividerText}
              requiredFields={
                isGuestRequired(index) ? { title: true, firstName: true, lastName: true } : {}
              }
              labels={{
                guestTitleFormatter,
                titleLabel,
                titlePlaceholder,
                firstNameLabel,
                lastNameLabel,
                removeLabel: removeGuest,
              }}
            />
          ))}
          {canAddGuest && (
            <AddGuestRow>
              <Button type="button" variant="outline" size="small" onClick={onAddGuest}>
                {addGuest}
              </Button>
            </AddGuestRow>
          )}
          {showNotes && (
            <BookingNotesCard
              checked={notesChecked}
              onToggle={(checked) => onNotesToggle?.(checked)}
              value={notesValue}
              onChange={(value) => onNotesChange?.(value)}
              labels={{ title: notesTitle, placeholder: notesPlaceholder }}
            />
          )}
        </FormGroup>
      )}
    </ContentPanelShell>
  );
}
